import useFunctionsUrl from './use-function-url';

// Removing getSubscribeUrl entirely since we use the hook directly inside useSubscribe
const projectId = 'mr-website-fb6db';

export type SubscriptionRequest = {
  email: string;
  firstName: string;
  lastName: string;
  // Human verification fields
  turnstileToken?: string;
  honeypot?: string;
  formTs?: number;
  testMode?: boolean;
}

export type SubscriptionResult = {
  success: boolean;
  message: string;
  error?: string;
  testMode?: boolean;
}

/// <summary>
/// Custom hook to subscribe a user to the newsletter.
/// </summary>
export default function useSubscribe(testMode = false): (request: SubscriptionRequest) => Promise<SubscriptionResult> {
  const baseUrl = useFunctionsUrl();
  const url = `${baseUrl}/subscribeV2`;

  async function subscribe(request: SubscriptionRequest): Promise<SubscriptionResult> {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: request.email.toLowerCase().trim(),
          firstName: request.firstName.trim(),
          lastName: (request.lastName || '').trim(),
          // Pass human verification fields through
          turnstileToken: request.turnstileToken || '',
          honeypot: request.honeypot || '',
          formTs: request.formTs || undefined,
          testMode: testMode ?? request.testMode ?? false,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        return {
          success: false,
          message: result.error || 'Subscription failed - please try again later',
        };
      }
      return {
        success: true,
        message: result.message || 'Successfully subscribed to the newsletter!',
        testMode,
      };
    }
    catch (error: any) {
      if (error.name === 'TypeError') {
        return {
          success: false,
          message: 'Subscription service temporarily unavailable - please try again later',
          error: 'Network error',
        };
      }
      if (error.status === 400) {
        return {
          success: false,
          message: 'Invalid form data - please check your input',
          error: error.message || 'Invalid argument',
        };
      }
      return {
        success: false,
        message: 'Subscription failed - please try again later',
        error: error.message || 'Unknown error',
      };
    }
  };

  return subscribe;
}